/** Locale constants shared by next-intl routing and Payload CMS localization. */

export const locales = ['en-us', 'es-mx', 'en-ca', 'fr-ca', 'it-it', 'nl-nl', 'de-de'] as const
export type Locale = (typeof locales)[number]

export const defaultLocale: Locale = 'en-us'

/**
 * All locales that should appear in hreflang alternate links.
 * Includes locales served by both Nexus and the legacy site.
 */
export const hreflangLocales = [
    'de-de',
    'en-ca',
    'en-gb',
    'en-us',
    'es-es',
    'es-mx',
    'fr-ca',
    'fr-fr',
    'it-it',
    'nl-nl',
] as const

/**
 * Every locale **the platform** serves — nexus's own `locales` plus the ones only the
 * legacy app still renders. Deliberately the same set as `hreflangLocales` rather than
 * a second list beside it: an hreflang alternate exists for exactly those locales
 * something serves, so the two are one fact seen twice, and a locale retired from the
 * platform has to leave in one edit. Matches ui-react's `supportedLocales`
 * (`packages/wanderu-shellapp/src/config/baseApp.locales.js`).
 *
 * `Locale` stays the narrower type — the locales **this app** routes — so a value
 * typed `PlatformLocale` cannot be mistaken for one nexus renders.
 */
export type PlatformLocale = (typeof hreflangLocales)[number]

/**
 * Region-agnostic language → regional-variant priority map. Used to emit
 * `<link rel="alternate" hreflang="en">`-style entries that map a language
 * code to the "best" regional variant we serve for that language. The first
 * entry in each list that's still in `hreflangLocales` (and not noindex'd)
 * wins; if none survive, the language entry is dropped.
 */
export const langToLocaleOrder: Record<string, readonly string[]> = {
    en: ['en-us', 'en-ca', 'en-gb'],
    es: ['es-es', 'es-mx'],
    fr: ['fr-fr', 'fr-ca'],
    it: ['it-it'],
    nl: ['nl-nl'],
    de: ['de-de'],
}
